// eslint-disable-next-line import/no-unresolved
import { constants, text as textConfig } from '@config';
import Store from '@store';
import { transformNumberToStyle } from 'src/utils/format';

export function getOrderedListStyles () {
  return Store.getState().text.orderedList.styles;
}

export function getBulletListStyles () {
  return Store.getState().text.bulletList.styles;
}

export function shiftLetterSpacing (oldContent, newContent, cursorPosition, newLetters, oldLetterSpacing) {
  if (Number.isNaN(cursorPosition)) return oldLetterSpacing;
  if (newLetters === 0) return oldLetterSpacing;

  const realOldContentLength = getRealContentLength(oldContent);
  const realNewContentLength = getRealContentLength(newContent);
  const numOfDifferenceRealChars = Math.abs(realOldContentLength - realNewContentLength);
  const contentBeforeCursorPos = newLetters < 0
    ? oldContent.slice(0, cursorPosition)
    : oldContent.slice(0, cursorPosition - newLetters);
  const positionToShift = contentBeforeCursorPos.length - getRealContentLength(contentBeforeCursorPos);
  const realCursorPosition = cursorPosition - positionToShift;
  const newLetterSpacing = newLetters < 0
    ? removeLetterSpacingFromPosition(oldLetterSpacing, realCursorPosition, numOfDifferenceRealChars)
    : addLetterSpacingFromPosition(oldLetterSpacing, realCursorPosition, numOfDifferenceRealChars);

  return newLetterSpacing;
}

export function removeLetterSpacingFromPosition (oldLetterSpacing, position, length) {
  if (length === 0) return oldLetterSpacing;

  const newLetterSpacing = [...oldLetterSpacing];
  newLetterSpacing.splice(position, length);

  return newLetterSpacing;
}

export function addLetterSpacingFromPosition (oldLetterSpacing, position, length) {
  if (length === 0) return oldLetterSpacing;

  const newLetterSpacing = [...oldLetterSpacing];
  newLetterSpacing.splice(position - length, 0, ...new Array(length).fill(0));

  return newLetterSpacing;
}

export function mapCommonStylingToArray (letterSpacing, content, lengthToMap = 0) {
  const realLengthToMap = lengthToMap || getRealContentLength(content);
  if (letterSpacing?.length === realLengthToMap) return letterSpacing;
  if (letterSpacing?.length > realLengthToMap) return letterSpacing.slice(0, realLengthToMap);
  if (letterSpacing?.length < realLengthToMap) return [...letterSpacing, ...new Array(realLengthToMap - letterSpacing.length).fill(0)];
  if (!letterSpacing) return new Array(realLengthToMap).fill(0);
  if (!Number.isNaN(letterSpacing) && !Array.isArray(letterSpacing)) return new Array(realLengthToMap).fill(letterSpacing);

  return letterSpacing;
}

export function removeFakeCharsFromTextContent (content) {
  // Fake character is a '\n' character that placed after a non '\n' character
  // For example: 'abc\ndef' => '\n' is a fake character
  //              'abc\n\n\ndef' => only '\n' 3rd index is a fake character
  //              '\n\n\n\ndef' => no fake character here
  if (!content?.length) return content;

  // First letter is always real character
  const contentWithoutFakeChars = [content[0]];
  for (let i = 1; i < content.length; i++) {
    if (content[i] === '\n' && content[i - 1] !== '\n') continue;
    contentWithoutFakeChars.push(content[i]);
  }

  return contentWithoutFakeChars.join('');
}

export function getRealContentLength (content) {
  if (!content?.length) return 0;

  let realContentLength = 1;
  for (let i = 1; i < content.length; i++) {
    if (content[i] === '\n' && content[i - 1] !== '\n') continue;
    realContentLength += 1;
  }
  return realContentLength;
}

export function shiftPartialStyling (oldContent, newContent, cursorPosition, newLetters, oldStyles, parentStyle = {}) {
  // cursorPosition is the position of the cursor in the new content
  const numOfDifferenceRealChars = getRealContentLength(newContent) - getRealContentLength(oldContent);
  if (numOfDifferenceRealChars === 0) return oldStyles;

  const contentBeforeCursorPosition = newLetters < 0
    ? oldContent.slice(0, cursorPosition)
    : oldContent.slice(0, cursorPosition - newLetters);
  const positionToShift = contentBeforeCursorPosition.length - getRealContentLength(contentBeforeCursorPosition);
  const realCursorPosition = cursorPosition - positionToShift;
  const styles = spreadStyles(oldStyles, oldContent, parentStyle);
  const shiftedSpreadStyles = numOfDifferenceRealChars < 0
    ? removeStyleFromPosition(styles, realCursorPosition, Math.abs(numOfDifferenceRealChars))
    : addStyleToPosition(styles, realCursorPosition, Math.abs(numOfDifferenceRealChars));
  const narrowedStyles = narrowStyles(shiftedSpreadStyles);
  const splittedStyles = splitStyles(narrowedStyles);
  const cleanedStyles = removeRedundantStyles(splittedStyles, parentStyle);

  return cleanedStyles;
}

export function spreadStyles (styles, content, parentStyle) {
  const realContentLength = getRealContentLength(content);
  const arrayStyles = {};

  for (const attribute in constants.text.partialStylingAttributeMap) {
    arrayStyles[attribute] = new Array(realContentLength).fill(parentStyle[attribute.replaceAll('-', '_')]);
  }

  for (const style of styles) {
    const { start, end, attribute, value } = style;
    arrayStyles[attribute] = replaceItemsInArray(arrayStyles[attribute], start, end, value);
  }

  // Filter array containing falsy values
  const sanitizedArrayStyles = Object.entries(arrayStyles).filter(([key, value]) => value.some(Boolean));

  return Object.fromEntries(sanitizedArrayStyles);
}

export function narrowStyles (spreadStyle) {
  let narrowStyles = [];
  Object.entries(spreadStyle).forEach(([attribute, values]) => {
    narrowStyles = narrowStyles.concat(convertStyleArrayToObject(attribute, values));
  });

  return narrowStyles;
}

export function convertStyleArrayToObject (attribute, styleArray) {
  styleArray.push(undefined);
  const arrayObject = [];
  let temp = {};
  for (let i = 0; i < styleArray.length; i++) {
    const style = styleArray[i];
    if (!style) continue;

    temp = {
      start: i,
      end: i + 1,
      attribute,
      value: style
    };

    for (let j = i + 1; j < styleArray.length; j++) {
      if (styleArray[j] === temp.value) {
        temp.end = j + 1;
      } else {
        i = j - 1;
        arrayObject.push({ ...temp });
        break;
      }
    }
  }
  styleArray.pop();
  return arrayObject;
}

export function replaceItemsInArray (array, start, end, value, exception) {
  for (let i = start; i < end; i++) {
    if (exception && exception !== array[i]) continue;
    array[i] = value;
  }
  return array;
}

export function spliceSpreadStyles (spreadStyles, startIndex, numOfItemsToRemove) {
  const newSpreadStyles = { ...spreadStyles };
  Object.keys(newSpreadStyles).forEach((attribute) => {
    newSpreadStyles[attribute].splice(startIndex, numOfItemsToRemove);
  });

  return newSpreadStyles;
}

export function removeStyleFromPosition (spreadStyles, position, length) {
  const shiftedSpreadStyles = { ...spreadStyles };
  Object.keys(shiftedSpreadStyles).forEach((attribute) => {
    shiftedSpreadStyles[attribute].splice(position, length);
  });

  return shiftedSpreadStyles;
}

export function addStyleToPosition (spreadStyles, position, length) {
  if (length === 0) return spreadStyles;
  const shiftedSpreadStyles = { ...spreadStyles };
  Object.keys(shiftedSpreadStyles).forEach((attribute) => {
    const styleValueToAdd = shiftedSpreadStyles[attribute][position - length - 1];
    shiftedSpreadStyles[attribute].splice(position - length, 0, ...new Array(length).fill(styleValueToAdd));
  });

  return shiftedSpreadStyles;
}

export function addStyleToLastPosition (spreadStyles, styleToAdd) {
  Object.keys(spreadStyles).forEach((attribute) => {
    spreadStyles[attribute].push(styleToAdd);
  });

  return spreadStyles;
}

export function adjustSpreadStylesLength (spreadStyles, lengthToAdjust) {
  const currentLength = getRealContentLengthFromSpreadStyle(spreadStyles);

  if (lengthToAdjust > currentLength) {
    const diff = lengthToAdjust - currentLength;
    const stylesToAdd = new Array(diff).fill(null);
    Object.keys(spreadStyles).forEach((attribute) => {
      spreadStyles[attribute].push(...stylesToAdd);
    });
  } else if (lengthToAdjust < currentLength) {
    Object.keys(spreadStyles).forEach((attribute) => {
      spreadStyles[attribute] = spreadStyles[attribute].slice(0, lengthToAdjust);
    });
  }

  return spreadStyles;
}

export function adjustLetterSpacingLength (letterSpacing, lengthToAdjust) {
  const currentLength = letterSpacing.length;

  if (lengthToAdjust > currentLength) {
    const diff = lengthToAdjust - currentLength;
    const stylesToAdd = new Array(diff).fill(0);
    letterSpacing.push(...stylesToAdd);
  } else if (lengthToAdjust < currentLength) {
    letterSpacing = letterSpacing.slice(0, lengthToAdjust);
  }

  return letterSpacing;
}

export function splitStyles (styles) {
  const result = [];
  for (const currentStyle of styles) {
    const influencedStyles = styles.filter(style => {
      const isInside = style.start >= currentStyle.start && style.end <= currentStyle.end;
      const isLeftOverlapping = style.start < currentStyle.start && style.end > currentStyle.start;
      const isRightOverlapping = style.start < currentStyle.end && style.end > currentStyle.end;

      return isInside || isLeftOverlapping || isRightOverlapping;
    });

    const indexToBreak = [currentStyle.start, currentStyle.end];
    influencedStyles.forEach(style => {
      indexToBreak.push(style.start);
      indexToBreak.push(style.end);
    });

    const sortedIndexToBreak = [...new Set(indexToBreak)]
      .sort((a, b) => a - b)
      .filter(index => index >= currentStyle.start && index <= currentStyle.end);

    for (let i = 1; i < sortedIndexToBreak.length; i++) {
      result.push({
        start: sortedIndexToBreak[i - 1],
        end: sortedIndexToBreak[i],
        attribute: currentStyle.attribute,
        value: currentStyle.value
      });
    }
  }

  return sortStyle(result);
}

export function sortStyle (styles) {
  const clone = styles.slice();
  return clone.sort((a, b) => a.start === b.start
    ? (a.end - a.start) > (b.end - b.start)
    : (a.start - b.start)
  );
}

export function removeRedundantStyles (styles, parentStyle = {}) {
  const filterStyle = [];
  for (const style of styles) {
    const isDuplicatedWithParentStyle = style.value === parentStyle[style.attribute.replaceAll('-', '_')];
    if (isDuplicatedWithParentStyle) continue;
    filterStyle.push(style);
  }

  return filterStyle;
}

export function mapTextToStyle (text) {
  const styleObject = {};
  for (const attribute in constants.text.partialStylingAttributeMap) {
    const defaultStyle = constants.text.partialStylingAttributeMap[attribute];
    styleObject[attribute.replaceAll('-', '_')] = text[attribute.replaceAll('-', '_')] || defaultStyle;
  }
  return styleObject;
}

export function concatSpreadStyles (spreadStylesA, spreadStylesB, parentStyleA = {}, parentStyleB = {}) {
  const result = { ...spreadStylesA };
  const allKeys = [...new Set(Object.keys(spreadStylesA).concat(Object.keys(spreadStylesB)))];
  const lengthA = getRealContentLengthFromSpreadStyle(spreadStylesA);
  const lengthB = getRealContentLengthFromSpreadStyle(spreadStylesB);

  allKeys.forEach((attribute) => {
    const styleKey = attribute.replaceAll('-', '_');
    const toConcatA = result[attribute]?.length
      ? result[attribute]
      : new Array(lengthA).fill(parentStyleA[styleKey] || undefined);

    const toConcatB = spreadStylesB[attribute]?.length
      ? spreadStylesB[attribute]
      : new Array(lengthB).fill(parentStyleA[styleKey] || undefined);

    result[attribute] = toConcatA.concat(toConcatB);
  });

  return result;
}

export function getRealContentLengthFromSpreadStyle (spreadStyle) {
  const keys = Object.keys(spreadStyle);
  if (!keys?.length) return 0;

  return spreadStyle[keys[0]].length || 0;
}

export function calculateOrderContent (orderedArray, orderedStart, orderedStyleArray) {
  const orderedListStyles = getOrderedListStyles();
  orderedStyleArray = orderedStyleArray || new Array(orderedArray.length).fill(constants.text.orderedList.defaultStyleId);

  let levelTracking = {};
  for (let j = 1; j <= constants.text.orderedList.maxLevel; j++) {
    levelTracking[j] = j === 1 ? orderedStart : 1;
  }

  const mark = '.';
  const initialTrack = Object.assign({}, levelTracking);
  const result = [];

  for (let i = 0; i < orderedArray.length; i++) {
    const level = orderedArray[i];
    const style = orderedStyleArray[i];
    const styleChanged = i > 0 && orderedStyleArray[i] !== orderedStyleArray[i - 1];

    if (level === 0) {
      // Reset the tracking value if level is 0
      levelTracking = Object.assign({}, initialTrack);
      result.push(0);
    } else {
      if (styleChanged) {
        levelTracking = Object.assign({}, initialTrack);
      }

      const numberStyle = orderedListStyles[style].symbols[level - 1];
      // Order of level 1 will start from orderedStart
      const order = levelTracking[level];

      if (numberStyle === 'nested-numbers') {
        let string = '';
        for (let k = 1; k <= level; k++) {
          if (k > 1) string += mark;
          string += k === level ? levelTracking[k].toString() : Math.max((levelTracking[k] - 1), 1).toString();
        }
        result.push(string);
      } else {
        result.push(transformNumberToStyle(order, numberStyle));
      }

      levelTracking[level] += 1;

      for (let j = level + 1; j <= constants.text.orderedList.maxLevel; j++) {
        levelTracking[j] = j === 1 ? orderedStart : 1;
      }
    }
  }

  return result;
}

export function calculateBulletContent (bulletArray, bulletStyleArray, customBulletSymbolArray = []) {
  bulletStyleArray = bulletStyleArray || new Array(bulletArray.length).fill(constants.text.bulletPoints.defaultStyleId);
  const bulletListStyles = getBulletListStyles();
  const result = [];

  for (let i = 0; i < bulletArray.length; i++) {
    const level = bulletArray[i];
    if (level === 0) {
      result.push(0);
    } else {
      if (level === 1 && customBulletSymbolArray[i]) {
        result.push(customBulletSymbolArray[i]);
      } else {
        if (bulletListStyles[bulletStyleArray[i]] === undefined) {
          result.push(0);
        } else {
          result.push(bulletListStyles[bulletStyleArray[i]].symbols[level - 1]);
        }
      }
    }
  }

  return result;
}

export function isListShortcut (content, selectionStart, shortcut) {
  if (selectionStart < shortcut.length) return false;

  const currentCharIndex = selectionStart - 1;

  if (content[currentCharIndex] !== ' ') return false;
  if (content.slice(selectionStart - shortcut.length, selectionStart) !== shortcut) return false;
  if (currentCharIndex === shortcut.length - 1) return true;

  const isStartMatch = content[currentCharIndex - shortcut.length] === '\n';

  if (isStartMatch) return true;

  return false;
}

export function calculateOrderTracking (orderedArray, orderedStartArray, orderedStyleArray) {
  if (!orderedArray?.length || !orderedStartArray?.length || !orderedStyleArray?.length) return {};

  orderedStyleArray = orderedStyleArray || new Array(orderedArray.length).fill(constants.text.orderedList.defaultStyleId);

  const styleSet = [...new Set(orderedStyleArray)].filter(Boolean);
  const orderTracking = {};

  for (const style of styleSet) {
    orderTracking[style] = {};
    for (let j = 1; j <= constants.text.orderedList.maxLevel; j++) {
      orderTracking[style][j] = 1;
    }
  }

  for (let i = 0; i < orderedArray.length; i++) {
    const level = orderedArray[i];
    const style = orderedStyleArray[i];
    const orderedStart = orderedStartArray[i];

    if (level === 0) {
      continue;
    } else {
      orderTracking[style][level] = orderedStart
        ? orderedStart + 1
        : orderTracking[style][level] + 1;

      for (let j = level + 1; j <= constants.text.orderedList.maxLevel; j++) {
        orderTracking[style][j] = 1;
      }
    }
  }

  return orderTracking;
}

export function calculateAdvanceOrderContent (orderedArray, orderedStartArray, orderedStyleArray, orderTracking = {}, isContinuing = false, orderedNumberingStyleArray, orderedRegexArray) {
  orderedStyleArray = orderedStyleArray || new Array(orderedArray.length).fill(constants.text.orderedList.defaultStyleId);
  orderedNumberingStyleArray = orderedNumberingStyleArray || new Array(orderedArray.length).fill(0);
  orderedRegexArray = orderedRegexArray || new Array(orderedArray.length).fill('');

  const styleSet = [...new Set(orderedStyleArray)].filter(Boolean);

  for (const style of styleSet) {
    if (orderTracking[style]) continue;
    orderTracking[style] = {};
    for (let j = 1; j <= constants.text.orderedList.maxLevel; j++) {
      orderTracking[style][j] = 1;
    }
  }

  const result = [];

  for (let i = 0; i < orderedArray.length; i++) {
    const level = orderedArray[i];
    const style = orderedStyleArray[i];
    const orderedStart = orderedStartArray[i];
    const numberingStyle = orderedNumberingStyleArray[i];
    const regex = orderedRegexArray[i];
    const shouldSkip = isContinuing && i === 0;

    if (level === 0) {
      result.push(0);
    } else {
      const order = orderedStart || orderTracking[style][level];
      const orderAfterPatternReplace = createListName(order, style, level, orderTracking, numberingStyle, regex);
      const trackValueToIncrease = shouldSkip ? 0 : 1;

      result.push(orderAfterPatternReplace);

      orderTracking[style][level] = orderedStart
        ? orderedStart + trackValueToIncrease
        : orderTracking[style][level] + trackValueToIncrease;

      for (let j = level + 1; j <= constants.text.orderedList.maxLevel; j++) {
        if (shouldSkip) break;
        orderTracking[style][j] = 1;
      }
    }
  }

  return result;
}

export function getActiveStyles (startIndex, content, styles) {
  // Content          --------------[start-index---------end-index]--------
  // Styles           [--] [------------]-----[----]----[-----------]---[-----]
  //                  -----[------------]-----[----]
  // Active styles    --------------[---]-----[----]----[---------]
  //                  --------------[---]-----[----]

  if (!styles.length) return [{ start: startIndex, end: startIndex + content.length }];

  const group = ['font-size', 'font-weight', 'font-family', 'font-stretch', 'font-style'];
  const result = [];
  const endIndex = startIndex + content.length;

  for (let i = 0; i < styles.length; i++) {
    // Filter unwanted styles
    if (!group.includes(styles[i].attribute)) continue;

    // Skip styles that do not collapse with content
    if (styles[i].start >= endIndex || styles[i].end <= startIndex) continue;

    result.push({
      start: Math.max(styles[i].start, startIndex),
      end: Math.min(styles[i].end, endIndex),
      attribute: styles[i].attribute,
      value: styles[i].value
    });
  }

  return result;
}

export function createListName (order, style, level, orderTracking, numberingStyle, regex) {
  const orderedListStyles = getOrderedListStyles();
  const defaultStyle = orderedListStyles[constants.text.orderedList.defaultStyleId].symbols[level - 1];
  let { numberStyle, pattern } = extractParagraphStyleData(style, level, numberingStyle, regex, orderedListStyles, defaultStyle);
  const currentOrder = transformNumberToStyle(order, numberStyle);

  if (!pattern) return currentOrder;

  pattern = pattern.replaceAll('^#', currentOrder);

  for (let i = 1; i <= constants.text.orderedList.maxLevel; i++) {
    if (i === level) {
      pattern = pattern.replaceAll(`^${i}`, currentOrder);
      continue;
    }
    const previousOrder = Math.max(orderTracking[style][i] - 1, 1);
    pattern = pattern.replaceAll(`^${i}`, transformNumberToStyle(previousOrder, orderedListStyles[style]?.symbols[i - 1] || defaultStyle));
  }

  for (const key in constants.text.specialCharacters) {
    pattern = pattern.replaceAll(key, constants.text.specialCharacters[key]);
  }

  return pattern;
}

export function extractParagraphStyleData (style, level, numberingStyle, regex, orderedListStyles, defaultStyle) {
  return {
    numberStyle: textConfig.orderedList.stylesMapping[numberingStyle] || orderedListStyles[style]?.symbols[level - 1] || defaultStyle,
    pattern: regex || orderedListStyles[style]?.pattern[level - 1] || ''
  };
}

export function getSmartQuote (content, selectionStart, isDoubleQuote) {
  const openingQuote = isDoubleQuote ? constants.text.quoteCharacters.openingDoubleQuotes : constants.text.quoteCharacters.openingSingleQuote;
  const closingQuote = isDoubleQuote ? constants.text.quoteCharacters.closingDoubleQuotes : constants.text.quoteCharacters.closingSingeQuote;
  const straightQuote = isDoubleQuote ? constants.text.quoteCharacters.straightDoubleQuotes : constants.text.quoteCharacters.straightSingeQuote;

  if (!content?.length) return false;

  const currentCharIndex = selectionStart - 1;

  if (content[currentCharIndex] !== straightQuote) return false;
  if (currentCharIndex === 0) return openingQuote;
  if (/\s/.test(content[currentCharIndex - 1])) return openingQuote;

  return closingQuote;
}

export function insertParentStyles (startIndex, content, activeStyles, parentFontSize, parentFontFamily, parentFontWeight, parentFontStretch, parentFontStyle) {
  // This method will fill parts of the content that do not have partial style with parent's style
  // 1,2,3,4 are the segments of content that do not have partial style
  // 0 is the partial style of the content
  // Content          ------[---------------------------------------------]-------
  // Active styles          -----1----[-0-]--2---[--0-]--3--[-----0---]-4---------
  // Inserted styles        [----1---][-0-][-2--][--0-][-3-][-----0---][4-]-------

  // Base case
  if (!activeStyles.length) {
    return [{
      start: startIndex,
      end: startIndex + content.length,
      content,
      'font-size': parentFontSize,
      'font-weight': parentFontWeight,
      'font-family': parentFontFamily,
      'font-stretch': parentFontStretch,
      'font-style': parentFontStyle
    }];
  }

  const insertedStyles = [];

  for (let i = 0; i < activeStyles.length; i++) {
    const lastElement = insertedStyles[insertedStyles.length - 1];
    const style = activeStyles[i];
    let isDuplicated = false;

    if (!lastElement) {
      // Fill the first gap with parent style [Segment 1]
      insertedStyles.push({
        start: startIndex,
        end: style.start,
        content: content.slice(startIndex - startIndex, style.start - startIndex),
        'font-size': parentFontSize,
        'font-weight': parentFontWeight,
        'font-family': parentFontFamily,
        'font-stretch': parentFontStretch
      });
    } else if (lastElement.end < style.start) {
      // Fill the gap between partial active styles with parent style [Segment 2, 3]
      insertedStyles.push({
        start: lastElement.end,
        end: style.start,
        content: content.slice(lastElement.end - startIndex, style.start - startIndex),
        'font-size': parentFontSize,
        'font-weight': parentFontWeight,
        'font-family': parentFontFamily,
        'font-stretch': parentFontStretch
      });
    } else if (lastElement.end === style.end && lastElement.start === style.start) {
      // If there are more partial styles for the same start index or end index
      // Override parent style with partial style
      lastElement[style.attribute] = style.value;
      isDuplicated = true;
    }

    // Always push the partial style [Partial style 0]
    isDuplicated || insertedStyles.push({
      start: style.start,
      end: style.end,
      content: content.slice(style.start - startIndex, style.end - startIndex),
      'font-size': style.attribute === 'font-size' ? style.value : parentFontSize,
      'font-weight': style.attribute === 'font-weight' ? style.value : parentFontWeight,
      'font-family': style.attribute === 'font-family' ? style.value : parentFontFamily,
      'font-stretch': style.attribute === 'font-stretch' ? style.value : parentFontStretch
    });

    // Fill the last gap with parent style [Segment 4]
    if (i === activeStyles.length - 1 && style.end < startIndex + content.length) {
      insertedStyles.push({
        start: style.end,
        end: startIndex + content.length,
        content: content.slice(style.end - startIndex, content.length),
        'font-size': parentFontSize,
        'font-weight': parentFontWeight,
        'font-family': parentFontFamily,
        'font-stretch': parentFontStretch
      });
    }
  }

  return insertedStyles;
}

export function insertFontSizeStyle (startIndex, content, activeStyles, parentFontSize) {
  // This method will fill parts of the content that do not have partial style with parent's style
  // 1,2,3,4 are the segments of content that do not have partial style
  // 0 is the partial style of the content
  // Content          ------[---------------------------------------------]-------
  // Active styles          -----1----[-0-]--2---[--0-]--3--[-----0---]-4---------
  // Inserted styles        [----1---][-0-][-2--][--0-][-3-][-----0---][4-]-------

  // Base case
  if (!activeStyles.length) {
    return [{
      start: startIndex,
      end: startIndex + content.length,
      attribute: 'font-size',
      value: parentFontSize
    }];
  }

  const insertedStyles = [];

  for (let i = 0; i < activeStyles.length; i++) {
    const lastElement = insertedStyles[insertedStyles.length - 1];
    const style = activeStyles[i];
    let isDuplicated = false;

    if (!lastElement) {
      // Fill the first gap with parent style [Segment 1]
      insertedStyles.push({
        start: startIndex,
        end: style.start,
        attribute: 'font-size',
        value: parentFontSize
      });
    } else if (lastElement.end < style.start) {
      // Fill the gap between partial active styles with parent style [Segment 2, 3]
      insertedStyles.push({
        start: lastElement.end,
        end: style.start,
        attribute: 'font-size',
        value: parentFontSize
      });
    } else if (lastElement.end === style.end && lastElement.start === style.start) {
      // If there are more partial styles for the same start index or end index
      // Override parent style with partial style
      lastElement[style.attribute] = style.value;
      isDuplicated = true;
    }

    // Always push the partial style [Partial style 0]
    isDuplicated || insertedStyles.push({
      start: style.start,
      end: style.end,
      attribute: 'font-size',
      value: style.attribute === 'font-size' ? style.value : parentFontSize
    });

    // Fill the last gap with parent style [Segment 4]
    if (i === activeStyles.length - 1 && style.end < startIndex + content.length) {
      insertedStyles.push({
        start: style.end,
        end: startIndex + content.length,
        content: content.slice(style.end - startIndex, content.length),
        value: parentFontSize
      });
    }
  }

  return insertedStyles;
}
